'use client'

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'

/**
 * Shared MUI-style confirmation dialog (per task #895). Replaces native
 * window.confirm() across the admin app.
 *
 *   const confirm = useConfirm()
 *   if (await confirm({ title: 'Delete?', body: '...' })) doIt()
 */

interface ConfirmOptions {
  title?: string
  body?: ReactNode
  confirmText?: string
  cancelText?: string
  confirmColor?: 'primary' | 'error' | 'warning' | 'success' | 'info'
}

type Resolver = (ok: boolean) => void

const ConfirmCtx = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState<ConfirmOptions>({})
  const resolverRef = useRef<Resolver | null>(null)

  const ask = useCallback((o: ConfirmOptions) => {
    setOpts(o)
    setOpen(true)
    return new Promise<boolean>(resolve => { resolverRef.current = resolve })
  }, [])

  const close = useCallback((ok: boolean) => {
    setOpen(false)
    const r = resolverRef.current
    resolverRef.current = null
    r?.(ok)
  }, [])

  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      {/* WEB-027: this dialog is mounted globally (ConfirmProvider in Providers.tsx)
          and pops up on top of every entity screen, including inside a freshly-opened
          popout window. Without disableAutoFocus/disableEnforceFocus, MUI's Modal
          auto-focuses this dialog on mount and force-restores focus to it whenever
          focus tries to leave — both async, and both race the user's very next real
          click (e.g. the "Convert" button in "Convert to Invoice?"), so the first
          click only moves focus and a second click is needed to actually register. */}
      <Dialog open={open} onClose={() => close(false)} maxWidth='xs' fullWidth disableAutoFocus disableEnforceFocus>
        <DialogTitle>{opts.title ?? 'Are you sure?'}</DialogTitle>
        <DialogContent>
          {typeof opts.body === 'string'
            ? <DialogContentText>{opts.body}</DialogContentText>
            : opts.body}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => close(false)} color='inherit'>{opts.cancelText ?? 'Cancel'}</Button>
          <Button onClick={() => close(true)} color={opts.confirmColor ?? 'primary'} variant='contained'>{opts.confirmText ?? 'Confirm'}</Button>
        </DialogActions>
      </Dialog>
    </ConfirmCtx.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx
}
